// src/ui/components/tideAdvisor.ts — Victorian Tide Advisor & Chronometer Component (FR-REC-001..003)
// Renders an interactive home screen widget aware of time since last practice,
// time of day (circadian tide phases), day of week, and interactive session duration pills.

import {
  ScheduleAdvisor,
  SessionDurationMinutes,
  ActivityRecommendation,
  PracticeContext,
} from '../../storage/scheduleAdvisor';
import { ProfileManager } from '../../storage/profileManager';
import { renderCornerFiligree, renderCompassWatermark } from './ornateHelpers';

export class TideAdvisor {
  private container: HTMLElement;
  private selectedDuration: SessionDurationMinutes;
  private unsubscribeProfile?: () => void;

  constructor() {
    this.container = document.createElement('section');
    this.container.id = 'tide-advisor-station';
    this.container.className = 'vellum-surface';
    this.container.setAttribute('aria-label', 'Victorian Tide Advisor and Recommended Activity');

    // Default duration contextually selected
    this.selectedDuration = ScheduleAdvisor.getDefaultDuration();

    this.container.style.cssText = `
      position: relative;
      margin: 24px 0;
      padding: 26px 24px;
      border-radius: 16px;
      border: 2px solid var(--gilded-brass);
      box-shadow: 0 12px 36px rgba(10, 25, 47, 0.28), inset 0 0 35px rgba(229, 169, 60, 0.08);
      touch-action: manipulation;
    `;

    this.render();
    this.attachEvents();

    // Re-render when profile changes (Tudor vs Tester)
    this.unsubscribeProfile = ProfileManager.onProfileChange(() => {
      this.render();
      this.attachEvents();
    });
  }

  getElement(): HTMLElement {
    return this.container;
  }

  destroy(): void {
    this.unsubscribeProfile?.();
  }

  getSelectedDuration(): SessionDurationMinutes {
    return this.selectedDuration;
  }

  setDuration(duration: SessionDurationMinutes): void {
    this.selectedDuration = duration;
    this.render();
    this.attachEvents();
  }

  private render(): void {
    const context: PracticeContext = ScheduleAdvisor.getPracticeContext();
    const recommendation: ActivityRecommendation = ScheduleAdvisor.getRecommendation(
      this.selectedDuration
    );
    const alternatives = ScheduleAdvisor.getAlternativeRecommendations(this.selectedDuration);

    const isTester = ProfileManager.isTester();
    const durationOptions: Array<{ mins: SessionDurationMinutes; label: string; icon: string }> = [
      { mins: 5, label: '5m Quick Dip', icon: '⚡' },
      { mins: 10, label: '10m Standard Dive', icon: '🧭' },
      { mins: 20, label: '20m Deep Expedition', icon: '🌊' },
      { mins: 45, label: '45m Mock Trial', icon: '🏛️' },
    ];

    this.container.innerHTML = `
      ${renderCornerFiligree()}
      ${renderCompassWatermark()}

      <!-- Top Header Row -->
      <div style="display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 12px; margin-bottom: 18px; position: relative; z-index: 2;">
        <div>
          <div class="atelier-eyebrow-pill" style="margin-bottom: 6px;">
            <span>🕰️</span>
            <span>CHRONOMETER &amp; TIDE ADVISOR</span>
            ${isTester ? '<span style="color: #0369A1; font-weight: 800;">[SANDBOX MODE]</span>' : ''}
          </div>
          <h3 style="font-family: var(--font-heading); font-size: 1.45rem; color: var(--morris-indigo); margin: 0; line-height: 1.2;">
            ${context.dayName} Expedition Logbook
          </h3>
          <div style="font-size: 0.85rem; color: #5A6A80; font-weight: 600; margin-top: 4px;">
            ${context.tidePhase.cognitiveNote}
          </div>
        </div>

        <!-- Telemetry & Recency Pills -->
        <div style="display: flex; gap: 8px; flex-wrap: wrap; align-items: center;">
          <span id="tide-phase-pill" class="domain-tag" style="background: rgba(229, 169, 60, 0.15); border-color: var(--gilded-brass); color: var(--morris-indigo); font-weight: 700; padding: 6px 12px; font-size: 0.85rem;">
            ${context.tidePhase.badge}
          </span>
          <span id="recency-pill" class="domain-tag" style="background: rgba(26, 54, 93, 0.08); border-color: var(--vellum-edge); color: #2D3748; font-weight: 600; padding: 6px 12px; font-size: 0.85rem;">
            ⏳ Last Dive: ${context.timeSinceLastPracticeFormatted}
          </span>
          <span id="daily-quota-pill" class="domain-tag" style="background: ${context.isDailyLimitReached ? 'rgba(197, 48, 48, 0.1)' : 'rgba(30, 90, 60, 0.1)'}; border-color: ${context.isDailyLimitReached ? '#E53E3E' : '#1E5A3C'}; color: ${context.isDailyLimitReached ? '#C53030' : '#1E5A3C'}; font-weight: 700; padding: 6px 12px; font-size: 0.85rem;">
            ⏱️ ${context.todayPracticeMinutes} / ${context.dailyLimitMinutes}m Daily Practice
          </span>
        </div>
      </div>

      <!-- Duration Selector Pills -->
      <div style="margin-bottom: 20px; position: relative; z-index: 2;">
        <div style="font-size: 0.85rem; font-weight: 800; color: var(--morris-indigo); margin-bottom: 8px; text-transform: uppercase; letter-spacing: 0.05em;">
          Select Available Dive Time:
        </div>
        <div style="display: flex; gap: 10px; flex-wrap: wrap;" role="radiogroup" aria-label="Dive Duration Selector">
          ${durationOptions
            .map(
              (opt) => `
              <button
                class="touch-target duration-pill-btn ${opt.mins === this.selectedDuration ? 'active-duration' : ''}"
                data-mins="${opt.mins}"
                role="radio"
                aria-checked="${opt.mins === this.selectedDuration}"
                style="
                  min-height: 48px;
                  min-width: 120px;
                  padding: 8px 16px;
                  border-radius: 999px;
                  font-family: var(--font-heading);
                  font-size: 0.9rem;
                  font-weight: 700;
                  cursor: pointer;
                  display: inline-flex;
                  align-items: center;
                  justify-content: center;
                  gap: 8px;
                  transition: all 0.2s ease;
                  background: ${opt.mins === this.selectedDuration ? 'linear-gradient(180deg, #FF6B4A 0%, #D94828 100%)' : 'linear-gradient(180deg, #FFFDF9 0%, #EFE5CE 100%)'};
                  color: ${opt.mins === this.selectedDuration ? '#FFFFFF' : 'var(--morris-indigo)'};
                  border: 2px solid ${opt.mins === this.selectedDuration ? 'var(--deep-coral)' : 'var(--gilded-brass)'};
                  box-shadow: ${opt.mins === this.selectedDuration ? '0 4px 12px rgba(255, 107, 74, 0.35)' : '0 2px 6px rgba(10, 25, 47, 0.1)'};
                "
              >
                <span>${opt.icon}</span>
                <span>${opt.label}</span>
              </button>
            `
            )
            .join('')}
        </div>
      </div>

      <!-- Primary Recommendation Card -->
      <div id="primary-recommendation-card" style="
        background: linear-gradient(180deg, rgba(255, 253, 249, 0.98) 0%, rgba(245, 235, 206, 0.95) 100%);
        border: 2px solid var(--gilded-brass);
        border-radius: 14px;
        padding: 22px 24px;
        position: relative;
        z-index: 2;
        box-shadow: 0 6px 18px rgba(10, 25, 47, 0.15);
        display: grid;
        grid-template-columns: 1fr auto;
        gap: 20px;
        align-items: center;
      ">
        <div style="display: flex; flex-direction: column; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 10px; flex-wrap: wrap;">
            <span class="domain-tag brass" style="font-weight: 800;">
              RECOMMENDED EXPEDITION · ${recommendation.domainLabel}
            </span>
            <span class="domain-tag" style="background: rgba(30, 90, 60, 0.12); color: #1E5A3C; border-color: #1E5A3C; font-weight: 700;">
              🧭 +${recommendation.explorerPoints} Explorer Points
            </span>
          </div>

          <h4 id="rec-title" style="font-family: var(--font-heading); font-size: 1.35rem; color: var(--morris-indigo); margin: 4px 0 0 0;">
            ${recommendation.title}
          </h4>

          <p id="rec-description" style="font-size: 0.92rem; color: #333; margin: 0; line-height: 1.45;">
            ${recommendation.description}
          </p>

          <div style="font-size: 0.82rem; color: var(--seaweed-sage); font-style: italic; font-weight: 600;">
            💡 ${recommendation.rationale}
          </div>

          <!-- Clawey Speech Bubble -->
          <div style="
            display: flex;
            align-items: center;
            gap: 10px;
            background: rgba(255, 255, 255, 0.8);
            border: 1px dashed var(--gilded-brass);
            border-radius: 10px;
            padding: 8px 12px;
            margin-top: 6px;
          ">
            <img src="/assets/svg/clawey-icon-192.svg" alt="Clawey" style="width: 32px; height: 32px; flex-shrink: 0;" />
            <div id="rec-quote" style="font-size: 0.85rem; color: var(--morris-indigo); font-style: italic;">
              "${recommendation.claweyQuote}"
            </div>
          </div>
        </div>

        <!-- Launch Action CTA -->
        <div style="display: flex; flex-direction: column; align-items: center; gap: 8px; flex-shrink: 0;">
          <a
            href="${recommendation.stationRoute}"
            id="launch-recommended-btn"
            class="touch-target btn-coral"
            style="
              min-height: 52px;
              min-width: 180px;
              font-size: 1rem;
              font-weight: 800;
              padding: 10px 22px;
              text-align: center;
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 8px;
              box-shadow: 0 6px 16px rgba(255, 107, 74, 0.35);
              text-decoration: none;
            "
          >
            <span>Launch Dive</span>
            <span>&rarr;</span>
          </a>
          <span style="font-size: 0.78rem; color: #666; font-weight: 600;">
            Estimated time: ${recommendation.estimatedMinutes} min
          </span>
        </div>
      </div>

      <!-- Alternative Suggestions Strip -->
      <div style="margin-top: 16px; padding-top: 12px; border-top: 1px solid rgba(213, 197, 163, 0.6); position: relative; z-index: 2;">
        <div style="display: flex; align-items: center; justify-content: space-between; flex-wrap: wrap; gap: 8px;">
          <div style="font-size: 0.8rem; font-weight: 700; color: #5A6A80; text-transform: uppercase;">
            ⚓ Other Harbors Available:
          </div>
          <div style="display: flex; gap: 10px; flex-wrap: wrap;">
            ${alternatives
              .map(
                (alt) => `
                <a
                  href="${alt.stationRoute}"
                  class="touch-target alt-harbor-link"
                  style="
                    display: inline-flex;
                    align-items: center;
                    gap: 6px;
                    padding: 4px 12px;
                    min-height: 48px;
                    background: rgba(26, 54, 93, 0.05);
                    border: 1px solid var(--vellum-edge);
                    border-radius: 999px;
                    font-size: 0.82rem;
                    font-weight: 700;
                    color: var(--morris-indigo);
                    text-decoration: none;
                    transition: all 0.2s ease;
                  "
                >
                  <span>${alt.title}</span>
                  <span style="color: var(--seaweed-sage);">&rarr;</span>
                </a>
              `
              )
              .join('')}
          </div>
        </div>
      </div>
    `;
  }

  private attachEvents(): void {
    // Duration pill click handler
    const durationPills = this.container.querySelectorAll<HTMLButtonElement>('.duration-pill-btn');
    durationPills.forEach((pill) => {
      pill.addEventListener('click', () => {
        const mins = parseInt(pill.getAttribute('data-mins') ?? '10', 10) as SessionDurationMinutes;
        this.setDuration(mins);
      });
    });
  }
}
